import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISSED_KEY = 'gr-pwa-dismissed';

export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or already installed
    if (localStorage.getItem(DISMISSED_KEY)) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // iOS Safari doesn't fire beforeinstallprompt — show manual instructions
    const ios =
      /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) &&
      !(window as unknown as { MSStream: unknown }).MSStream;

    if (ios) {
      setIsIos(true);
      setTimeout(() => setVisible(true), 30_000); // show after 30s
      return;
    }

    // Android / Chrome — capture the native install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setVisible(true), 30_000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') dismiss();
    else setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:bottom-6 md:left-auto md:right-6 md:max-w-sm">
      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-card shadow-2xl">
        {/* Terracotta accent bar */}
        <div className="h-1 bg-gradient-to-r from-terracotta-400 to-terracotta-600" />

        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-terracotta-100">
              <Download className="h-5 w-5 text-terracotta-600" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Add to your home screen</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {isIos
                  ? 'Tap the share icon below, then "Add to Home Screen" for quick access.'
                  : 'Install Gracefully Rebuilt for quick access — no app store needed.'}
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-cream-100"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!isIos && (
            <button
              type="button"
              onClick={install}
              className="mt-3 w-full rounded-xl bg-terracotta-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terracotta-600 active:scale-[0.98]"
            >
              Install app
            </button>
          )}

          {isIos && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-cream-100 px-3 py-2.5 text-xs text-muted-foreground">
              <span>Tap</span>
              <span className="rounded bg-cream-200 px-1.5 py-0.5 font-mono text-[10px]">⎙ Share</span>
              <span>→ "Add to Home Screen"</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
